import React from 'react';
import { Columns3 } from 'lucide-react';

import type { DataTableColumn } from './DataTable';
import type { ColumnSortGuard, ColumnVisibilityState } from '../hooks/useColumnVisibility';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface ColumnPickerProps<T> {
  /** The FULL column array. Columns without `hideable` are skipped. */
  columns: Array<DataTableColumn<T>>;
  state: ColumnVisibilityState<T>;
  /** Lets hiding the sorted column fall back to a visible one in the same click. */
  sortGuard?: ColumnSortGuard;
}

/**
 * Column visibility control for a list page's `PageHeader` action row.
 *
 * Non-hideable columns are OMITTED, never rendered disabled: Radix skips
 * disabled items in roving focus, so a disabled item explains itself only to
 * sighted mouse users, and it would make the menu's navigable item count change
 * with sort state. "Reset to defaults" is the only disabled item, and only when
 * it would genuinely do nothing.
 */
export function ColumnPicker<T>({
  columns,
  state,
  sortGuard,
}: ColumnPickerProps<T>): React.ReactElement {
  // Layout-hidden columns (breakpoint or measured container width) are omitted
  // too: at that width the table does not render them, so a tick beside one
  // would be a lie and un-ticking it would silently reshape the wide layout.
  const hideable = columns.filter(
    (column) => column.hideable && column.label && !state.layoutHiddenKeys.has(column.key),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/*
          Icon-only, and `size="icon"` (36px) so it lines up with the Input and
          the primary buttons it shares the PageHeader row with. The hidden
          count moves into the accessible name and a dot, because a label that
          grows with state ("Columns · 3 hidden") changes the row's width and
          is what pushes a page's last button onto a second line.
        */}
        <Button
          variant="outline"
          size="icon"
          className="relative"
          aria-label={
            state.hiddenCount > 0 ? `Columns, ${state.hiddenCount} hidden` : 'Columns'
          }
        >
          <Columns3 aria-hidden="true" />
          {state.hiddenCount > 0 ? (
            <span
              aria-hidden="true"
              className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary"
            />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
        {hideable.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={!state.hiddenKeys.has(column.key)}
            // Keep the menu open so reshaping a table is one visit, not five.
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={() => state.toggle(column.key, sortGuard)}
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={state.isDefault} onSelect={() => state.reset(sortGuard)}>
          Reset to defaults
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
